import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, ArrowUpRight } from "lucide-react";

export default function PageHero({
  eyebrow,
  title,
  titleAccent,
  subtitle,
  breadcrumb = [],
  primaryCta,
  secondaryCta,
  rightSlot,
  testid = "page-hero",
}) {
  return (
    <section
      className="relative pt-28 md:pt-32 pb-16 md:pb-24 bg-white overflow-hidden"
      data-testid={testid}
    >
      <div className="absolute inset-0 bg-grid-pattern opacity-70 pointer-events-none" />
      <div className="absolute right-0 top-0 w-2/3 h-2/3 bg-gradient-to-bl from-electric/10 via-transparent to-transparent pointer-events-none" />
      <div className="container relative grid lg:grid-cols-12 gap-10 items-center">
        <div className={rightSlot ? "lg:col-span-7" : "lg:col-span-10"}>
          {breadcrumb.length > 0 && (
            <nav
              aria-label="Breadcrumb"
              className="flex items-center gap-2 text-xs uppercase tracking-widest text-zinc-500 mb-6"
            >
              {breadcrumb.map((b, i) => (
                <span key={b.label} className="flex items-center gap-2">
                  {b.href ? (
                    <Link to={b.href} className="hover:text-electric">
                      {b.label}
                    </Link>
                  ) : (
                    <span className="text-charcoal">{b.label}</span>
                  )}
                  {i < breadcrumb.length - 1 && <span>/</span>}
                </span>
              ))}
            </nav>
          )}
          {eyebrow && (
            <p className="text-overline text-electric mb-4">{eyebrow}</p>
          )}
          <motion.h1
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="font-display text-[2.4rem] sm:text-5xl lg:text-[4rem] leading-[1.04] font-extrabold text-charcoal text-balance"
          >
            {title}{" "}
            {titleAccent && (
              <span className="relative inline-block">
                <span className="relative z-10">{titleAccent}</span>
                <span className="absolute inset-x-0 bottom-1 h-3 bg-electric/30 -z-0" />
              </span>
            )}
          </motion.h1>
          {subtitle && (
            <p className="mt-6 text-lg md:text-xl text-zinc-600 max-w-2xl leading-relaxed">
              {subtitle}
            </p>
          )}
          {(primaryCta || secondaryCta) && (
            <div className="mt-8 flex flex-wrap items-center gap-3">
              {primaryCta && (
                <Link
                  to={primaryCta.to}
                  data-testid={primaryCta.testid}
                  className="inline-flex items-center gap-2 bg-electric text-white px-6 py-3.5 font-semibold hover:bg-electric-400 hover:-translate-y-0.5 transition-all shadow-lg rounded-xl"
                >
                  {primaryCta.label} <ArrowRight className="w-4 h-4" />
                </Link>
              )}
              {secondaryCta && (
                <Link
                  to={secondaryCta.to}
                  data-testid={secondaryCta.testid}
                  className="inline-flex items-center gap-2 bg-white border border-charcoal text-charcoal px-6 py-3.5 font-semibold hover:bg-charcoal hover:text-white transition-colors"
                >
                  {secondaryCta.label} <ArrowUpRight className="w-4 h-4" />
                </Link>
              )}
            </div>
          )}
        </div>
        {rightSlot && (
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.7 }}
            className="lg:col-span-5"
          >
            {rightSlot}
          </motion.div>
        )}
      </div>
    </section>
  );
}

export function SectionHeader({ eyebrow, title, description, align = "left" }) {
  return (
    <div
      className={`mb-12 ${align === "center" ? "text-center mx-auto max-w-3xl" : "grid lg:grid-cols-12 gap-10 items-end"}`}
    >
      <div className={align === "center" ? "" : "lg:col-span-8"}>
        {eyebrow && <p className="text-overline text-electric mb-3">{eyebrow}</p>}
        <h2 className="font-display text-4xl md:text-5xl font-extrabold text-charcoal text-balance">
          {title}
        </h2>
      </div>
      {description && (
        <p
          className={
            align === "center"
              ? "mt-4 text-zinc-600 text-lg leading-relaxed"
              : "lg:col-span-4 text-zinc-600 text-lg leading-relaxed"
          }
        >
          {description}
        </p>
      )}
    </div>
  );
}

export function FeatureGrid({ items, testidPrefix = "feature", dark = false }) {
  return (
    <div
      className={`grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px ${dark ? "bg-charcoal-700 border border-charcoal-700" : "bg-zinc-200 border border-zinc-200"}`}
    >
      {items.map((it, idx) => {
        const Icon = it.icon;
        return (
          <motion.div
            key={it.title}
            initial={{ opacity: 0, y: 10 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.35, delay: idx * 0.04 }}
            className={`group p-7 md:p-9 transition-colors ${
              dark
                ? "bg-charcoal-900 text-white hover:bg-charcoal"
                : "bg-white text-charcoal hover:bg-charcoal hover:text-white"
            }`}
            data-testid={`${testidPrefix}-${idx}`}
          >
            <div className="flex items-center justify-between mb-6">
              {Icon && (
                <span className="w-11 h-11 grid place-items-center bg-electric text-white">
                  <Icon className="w-5 h-5" strokeWidth={2.2} />
                </span>
              )}
              <span
                className={`text-overline ${dark ? "text-zinc-400" : "text-zinc-500 group-hover:text-zinc-300"}`}
              >
                0{idx + 1}
              </span>
            </div>
            <h3 className="font-display text-xl md:text-2xl font-bold leading-tight">
              {it.title}
            </h3>
            <p
              className={`mt-3 text-[15px] leading-relaxed ${dark ? "text-zinc-400" : "text-zinc-600 group-hover:text-zinc-300"}`}
            >
              {it.text}
            </p>
          </motion.div>
        );
      })}
    </div>
  );
}

export function NumberedStrip({ steps, testidPrefix = "step" }) {
  return (
    <ol className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-px bg-charcoal-700 border border-charcoal-700">
      {steps.map((s, i) => (
        <li
          key={s.title}
          className="bg-charcoal-900 p-7 md:p-8"
          data-testid={`${testidPrefix}-${i + 1}`}
        >
          <span className="font-display text-5xl font-black text-electric/30 leading-none block mb-6">
            {String(i + 1).padStart(2, "0")}
          </span>
          <h3 className="font-display text-xl font-bold text-white">{s.title}</h3>
          <p className="mt-3 text-sm leading-relaxed text-zinc-400">{s.text}</p>
        </li>
      ))}
    </ol>
  );
}
